import { useState } from 'react'

import { Toasts } from '@fourthwall/components'
import type { CreateVideoPostPayload } from '@fourthwall/services/dist/MembershipsCreatorApiClient'

import { useCheckMediaCreatedMutation } from '../../../hooks/useCheckMediaCreatedMutation'
import { useImageConfirmMutation } from '../../../hooks/useImageConfirmMutation'
import { useUploadImageMutation } from '../../../hooks/useUploadImageMutation'
import { uploadMedia } from '../../../utils/uploadMedia'
import { useCreateSubtitlesResource } from './useCreateSubtitlesResource'
import { useCreateVideoPostMutation } from './useCreateVideoPostMutation'

export const useCreateVideoPost = () => {
  const [isLoading, setLoading] = useState(false)
  const [mediaUploadProgress, setMediaUploadProgress] = useState(0)

  const { createVideoPostMutation } = useCreateVideoPostMutation()
  const { checkMediaCreatedMutation } = useCheckMediaCreatedMutation()

  const { uploadImageMutation } = useUploadImageMutation()
  const { imageConfirmMutation } = useImageConfirmMutation()

  const { createSubtitlesResource } = useCreateSubtitlesResource()

  const getSubtitlesResourceIdPayload = async (subtitles: File | boolean | undefined) => {
    if (typeof subtitles === 'object') {
      const id = await createSubtitlesResource(subtitles, () => {
        setLoading(false)

        throw new Error('Memberships: create subtitle error')
      })

      return id
    }

    return undefined
  }

  const createVideoPost = async (
    file: File,
    thumbnail: File,
    subtitles: File | undefined,
    payload: CreateVideoPostPayload,
    handleSuccess: (postId: number) => void,
  ) => {
    setLoading(true)

    const subtitlesResourceId = await getSubtitlesResourceIdPayload(subtitles)

    const { postId, videoDirectUploadData, imageDirectUploadData } =
      await createVideoPostMutation.mutateAsync([{ ...payload, subtitlesResourceId }], {
        onError: () => setLoading(false),
      })

    if (imageDirectUploadData) {
      await uploadImageMutation.mutateAsync([thumbnail, { ...imageDirectUploadData }], {
        onError: () => {
          setLoading(false)

          throw new Error('Memberships: create video post upload image error')
        },
      })

      await imageConfirmMutation.mutateAsync(
        [postId, { signedId: imageDirectUploadData.signedId }],
        {
          onError: () => {
            setLoading(false)

            throw new Error('Memberships: create video post confirm image error')
          },
        },
      )
    }

    if (videoDirectUploadData) {
      await uploadMedia(videoDirectUploadData.uploadUrl, file, (progress: number) => {
        setMediaUploadProgress(Number(progress.toFixed()))
      }).catch(() => {
        setLoading(false)

        Toasts.notify('Media upload error. Please try again.', { type: 'error' })
      })

      checkMediaCreatedMutation.mutate(postId, {
        onSuccess: () => {
          setMediaUploadProgress(0)
          setLoading(false)
          handleSuccess(postId)
        },
        onError: () => setLoading(false),
      })
    }
  }

  return {
    createVideoPost,
    isLoading,
    mediaUploadProgress,
  }
}
